/**
 * useAutoGrade — Custom hook for auto-grade trigger, polling, and result state.
 * LAUNCH:C6.P3
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { triggerAutoGrade, getAutoGradeStatus, retryAutoGrade } from '../services/autoGradeService';

const POLL_INTERVAL = 2000;
const TERMINAL_STATUSES = ['completed', 'failed', 'quota_exceeded'];

export default function useAutoGrade() {
  const [status, setStatus] = useState(null);
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [eventId, setEventId] = useState(null);
  const intervalRef = useRef(null);
  const mountedRef = useRef(true);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  /* c8 ignore start */
  const poll = useCallback(async (eid) => {
    try {
      const data = await getAutoGradeStatus(eid);
      if (!mountedRef.current) return;

      setStatus(data.status);

      if (data.status === 'completed') {
        setResult(data.grade_result || data);
        setIsLoading(false);
        stopPolling();
      } else if (data.status === 'failed') {
        setError(data.error || 'Grading failed');
        setIsLoading(false);
        stopPolling();
      } else if (data.status === 'quota_exceeded') {
        setIsLoading(false);
        stopPolling();
      }
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err.message || 'Failed to check status');
      setIsLoading(false);
      stopPolling();
    }
  }, [stopPolling]);
  /* c8 ignore stop */

  const startPolling = useCallback((eid) => {
    stopPolling();
    intervalRef.current = setInterval(() => poll(eid), POLL_INTERVAL);
  }, [poll, stopPolling]);

  const triggerGrade = useCallback(async (vaultItemId, imageUrl = null) => {
    setIsLoading(true);
    setError(null);
    setResult(null);
    setStatus('queued');

    try {
      const data = await triggerAutoGrade(vaultItemId, imageUrl);
      /* c8 ignore next */
      if (!mountedRef.current) return;

      if (data.status === 'quota_exceeded') {
        setStatus('quota_exceeded');
        setIsLoading(false);
        return;
      }

      setEventId(data.event_id);
      /* c8 ignore next */
      setStatus(data.status || 'queued');
      startPolling(data.event_id);
    } catch (err) {
      /* c8 ignore next */
      if (!mountedRef.current) return;
      setError(err.message || 'Failed to trigger grading');
      setStatus('failed');
      setIsLoading(false);
    }
  }, [startPolling]);

  const retryGrade = useCallback(async (eid) => {
    setIsLoading(true);
    setError(null);
    setResult(null);
    setStatus('queued');

    try {
      const data = await retryAutoGrade(eid);
      /* c8 ignore next */
      if (!mountedRef.current) return;

      setEventId(data.event_id);
      /* c8 ignore next */
      setStatus(data.status || 'queued');
      startPolling(data.event_id);
    } catch (err) {
      /* c8 ignore next */
      if (!mountedRef.current) return;
      setError(err.message || 'Failed to retry grading');
      setStatus('failed');
      setIsLoading(false);
    }
  }, [startPolling]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopPolling();
    };
  }, [stopPolling]);

  return {
    triggerGrade,
    retryGrade,
    status,
    result,
    isLoading,
    error,
    eventId,
  };
}
